import {Elements} from '@stripe/react-stripe-js';
import {PaymentRequest, Stripe, StripeElementsOptions} from '@stripe/stripe-js';
import React, {useEffect, useRef, useState} from 'react';
import {Helmet} from 'react-helmet';
import {useHistory} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import * as Sentry from '@sentry/react';
import {BottomSheet} from '../../../components/BottomSheet';
import MaybeStagingBadge from '../../../components/MaybeStagingBadge';
import useDocumentTitle from '../../../hooks/useDocumentTitle';
import usePaywallDismissTracking from '../../../hooks/usePaywallDismissTracking';
import usePaywallAnalytics from '../../../hooks/usePaywallAnalytics';
import PaymentIntentScreen from '../../../screens/StripeCustomCheckout/PaymentIntentScreen';
import Analytics from '../../../utils/Analytics';
import Environment from '../../../utils/Environment';
import {useQueryToken, useValueParams} from '../../../utils/dynamicCodes/hooks';
import {reportRepeatCheckout} from '../../../utils/dynamicCodes/redirects';
import TrialDetailsList from '../../../projects/emailConfirmation-0424/components/TrialDetailsList';
import {
  createCheckoutDetails,
  createPaymentRequest,
  createStripeInstance,
} from '../../../projects/emailConfirmation-0424/setupStripeForEmailConfirmationPaywall';
import {getEmailConfirmationProducts, ProductCode} from '../../../projects/emailConfirmation-0424/products';
import {allowedLocale} from '../../../projects/emailConfirmation-0424/screens/EmailConfirmationCheckoutScreen';
import '../../../projects/emailConfirmation-0424/screens/EmailConfirmation.css';
import '../../I03-name-personalization/videoBackground.css';
import '../../I03-name-personalization/screens/NameGreetingScreen.css';
import validateEmail from '../../../utils/validateEmail';
import {growthBook} from '../../../index';
import {sanitizeFirstName} from '../../I03-name-personalization/sanitizeFirstName';
import {ArmConfig} from '../arms';
import GraduationPlanCells from '../components/GraduationPlanCells';
import {tokenParam} from '../../../utils/linkOutSession';

export type GraduationPaywallScreenProps = {
  config: ArmConfig;
  initialOption: ProductCode;
  otherOption: ProductCode;
  isStudent: boolean;
};

const GraduationPaywallScreen = (props: GraduationPaywallScreenProps) => {
  useTranslation();
  const {config, initialOption, otherOption, isStudent} = props;
  const PAYWALL = config.paywallSlug;
  useDocumentTitle('Rise');
  const [stripeInstance, setStripeInstance] = useState<Stripe | null>(null);
  const token = useQueryToken();
  const localeParam: string = useValueParams('locale') ?? 'US';
  const locale = allowedLocale(localeParam);
  const firstName = sanitizeFirstName(useValueParams('firstName'));
  const history = useHistory();
  const [trialOption, setTrialOption] = useState<ProductCode>(initialOption);

  const reminderDaysBeforeParam = useValueParams('reminderDaysBefore');
  const reminderDaysBefore = reminderDaysBeforeParam ? parseInt(reminderDaysBeforeParam, 10) : undefined;

  const emailConfirmationProducts = getEmailConfirmationProducts();
  const product = emailConfirmationProducts[trialOption][locale];
  const isWeek = product.daysFree === 7;

  const [options, setOptions] = useState<StripeElementsOptions>({});
  const [paymentRequest, setPaymentRequest] = useState<PaymentRequest | undefined>(undefined);
  const [paymentRequestLoading, setPaymentRequestLoading] = useState<boolean>(true);
  const [clientSecret, setClientSecret] = useState<string>('');
  const [duplicateCustomer, setDuplicateCustomer] = useState<boolean>(false);
  const [chargeDate, setChargeDate] = useState<string>('');
  const [prepaidBlockMode, setPrepaidBlockMode] = useState<'disallow_try_again' | 'disallow_and_redirect' | null>(null);
  const [videoReady, setVideoReady] = useState<boolean>(false);

  // Standard paywall funnel events (View → Trial Selection → Purchase) via the shared hook.
  // No `legacy` map: this is a brand-new paywall, so there are no old per-screen dashboards
  // to keep alive. `arm` distinguishes the two graduation arms within each slug.
  const paywallAnalytics = usePaywallAnalytics({
    paywall: PAYWALL,
    baseProps: {email: token, locale, isStudent, arm: config.arm, hasFirstName: !!firstName},
  });

  // Reuse the shared dismiss/dwell instrumentation. markPurchased() suppresses the
  // dismiss beacon on the post-conversion navigation.
  const {markPurchased} = usePaywallDismissTracking({
    paywall: PAYWALL,
    properties: {
      email: token,
      locale,
      isStudent,
      arm: config.arm,
      hasFirstName: !!firstName,
    },
  });

  useEffect(() => {
    paywallAnalytics.viewed();
  }, [token, locale, isStudent, config.arm, firstName, paywallAnalytics]);

  useEffect(() => {
    const setupStripe = async () => {
      if (token && !stripeInstance) {
        const checkoutDetails = await createCheckoutDetails(token);
        if ('error' in checkoutDetails) {
          if (checkoutDetails.duplicateCustomer) {
            setDuplicateCustomer(true);
          } else {
            Analytics.track('Unable to set up Stripe Custom Checkout Page', {
              env: Environment.getVar('REACT_APP_ENV') ?? 'test',
              email: token,
            });
          }
        } else {
          const stripe = await createStripeInstance();
          if (stripe) {
            setStripeInstance(stripe);
          }
          setClientSecret(checkoutDetails.client_secret);
          setChargeDate(checkoutDetails.charge_date);
          setOptions(checkoutDetails.options);
        }
      }
    };
    if (token) {
      setupStripe();
    }
  }, [stripeInstance, token]);

  useEffect(() => {
    if (duplicateCustomer) {
      reportRepeatCheckout(product.code, token);
    }
  }, [duplicateCustomer, product.code, token]);

  useEffect(() => {
    if (stripeInstance) {
      if (paymentRequest) {
        paymentRequest.update({
          total: {label: product.sheetLabel, amount: product.price},
        });
      } else {
        createPaymentRequest(stripeInstance, product.code, product.price, product.sheetLabel, locale, PAYWALL).then(
          paymentRequest => {
            setPaymentRequest(paymentRequest);
            setPaymentRequestLoading(false);
          },
        );
      }
    }
  }, [paymentRequest, product.code, product.price, product.sheetLabel, stripeInstance, trialOption, locale, PAYWALL]);

  const handleTrialSelection = (option: ProductCode) => {
    paywallAnalytics.trialSelection({trialOption: option});
    setTrialOption(option);
  };

  const handlePurchaseSuccess = ({existingAccount: _existingAccount}: {existingAccount: boolean}) => {
    markPurchased();
    paywallAnalytics.purchaseSuccess({trialOption});
    const reminderParam = reminderDaysBefore ? `&reminderDaysBefore=${reminderDaysBefore}` : '';
    history.push(`${config.trialRoute}?${tokenParam(token)}days=${product.daysFree}${reminderParam}`);
  };

  const refToScroll = useRef<HTMLDivElement>(null);
  const onCreditCardChoice = () => {
    refToScroll.current?.scrollIntoView({behavior: 'smooth'});
  };

  const handlePrepaidDetected = (mode: 'disallow_try_again' | 'disallow_and_redirect') => {
    setPrepaidBlockMode(mode);
    Analytics.track('Card Not Supported Sheet Viewed', {
      code: product.code,
      env: Environment.getVar('REACT_APP_ENV') ?? 'test',
      email: token,
      mode,
    });
  };

  useEffect(() => {
    const tokenIsLikelyAnId = token && !validateEmail(token);
    if (token && tokenIsLikelyAnId) {
      try {
        Analytics.identify(Number(token));
        Analytics.track('Identified user via token', {token: Number(token)});
      } catch (error) {
        Analytics.track('Unable to identify user via token', {token});
        Sentry.captureException(error instanceof Error ? error : new Error(String(error)));
      }
    }
  }, [token]);

  return (
    <>
      <Helmet>
        <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no" />
      </Helmet>
      <div className="w-full h-screen mx-auto checkout-screen-background i03-name-greeting-screen">
        <a
          href="https://risesciencehelp.zendesk.com/hc/en-us"
          target="_blank"
          rel="noopener noreferrer"
          className="absolute top-4 right-4 z-20 text-white hover:opacity-80 transition-opacity font-semibold"
          onClick={() =>
            Analytics.track('Help Clicked', {
              env: Environment.getVar('REACT_APP_ENV') ?? 'test',
              email: token,
              paywall: PAYWALL,
              arm: config.arm,
            })
          }
        >
          Help
        </a>
        <div className="i03-video-placeholder" aria-hidden="true" />
        <video
          autoPlay
          muted
          playsInline
          loop={true}
          id="Rise"
          className="fixed top-0 left-0 w-full object-cover object-top i03-video-fade"
          style={{marginTop: '-5vh', opacity: videoReady ? 1 : 0}}
          onCanPlay={() => setVideoReady(true)}
        >
          <source
            src="https://nyx-content-6xgk79jynn.s3.amazonaws.com/onboarding/paywall-v1b-edit-1290x2796-compressed-fast.mp4"
            type="video/mp4"
          />
          Your browser does not support HTML5 video.
        </video>

        <div
          ref={refToScroll}
          style={{position: 'absolute', left: 0, top: '25vh', backgroundColor: 'transparent', height: 1, width: '100%'}}
        />
        <div className="checkout-screen-content">
          <div style={{minHeight: window.innerHeight}} className="h-screen w-full">
            <div className="pt-2 flex flex-col i03-name-greeting-stack">
              <GraduationPlanCells
                onSelect={handleTrialSelection}
                selection={trialOption}
                products={[
                  getEmailConfirmationProducts()[initialOption][locale],
                  getEmailConfirmationProducts()[otherOption][locale],
                ]}
                isStudent={isStudent}
                bigHeader={config.bigHeader(isWeek)}
                smallHeader={config.smallHeader(isWeek, firstName)}
                relabelCards={config.relabelCards}
              />
              <div style={{paddingTop: 12}}>
                <TrialDetailsList trialOption={getEmailConfirmationProducts()[trialOption][locale]} />
              </div>
              <div>
                {duplicateCustomer ? (
                  <div className="text-white">Your subscription is active!</div>
                ) : Object.keys(options).length && stripeInstance ? (
                  <div
                    className={growthBook.getFeatureValue('paywall_link_out_custom_cta', false) ? '' : 'pl-5 pr-5'}
                    style={
                      growthBook.getFeatureValue('paywall_link_out_custom_cta', false)
                        ? {marginLeft: 12, marginRight: 12}
                        : {}
                    }
                  >
                    <Elements stripe={stripeInstance} options={options}>
                      <PaymentIntentScreen
                        code={product.code}
                        paymentRequest={paymentRequest}
                        clientSecret={clientSecret}
                        daysFree={product.daysFree}
                        chargeDate={chargeDate}
                        dynamicCheckout={true}
                        onPurchaseSuccess={handlePurchaseSuccess}
                        onPrepaidDetected={handlePrepaidDetected}
                        paymentRequestLoading={paymentRequestLoading}
                        onCreditCardChoice={onCreditCardChoice}
                        ctaText={config.cta(isWeek)}
                        useUpdatedCreditCardLogic={true}
                        useCustomCTA={true}
                        customCTAText={config.cta(isWeek)}
                      />
                    </Elements>
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </div>
        <BottomSheet
          isOpen={prepaidBlockMode !== null}
          onClose={() => {
            Analytics.track('Card Not Supported Sheet Dismissed', {
              code: product.code,
              env: Environment.getVar('REACT_APP_ENV') ?? 'test',
              email: token,
              mode: prepaidBlockMode,
            });
            setPrepaidBlockMode(null);
          }}
        >
          <div className="flex flex-col items-center text-center px-2 pb-4">
            <div
              style={{
                width: 48,
                height: 48,
                borderRadius: '50%',
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: 16,
              }}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <rect x="2" y="5" width="20" height="14" rx="2" stroke="#999" strokeWidth="2" />
                <line x1="2" y1="10" x2="22" y2="10" stroke="#999" strokeWidth="2" />
              </svg>
            </div>
            <h3 className="text-white text-xl font-bold mb-2">This card isn't supported</h3>
            {prepaidBlockMode === 'disallow_try_again' ? (
              <>
                <p className="text-gray-400 text-sm mb-6" style={{maxWidth: 280}}>
                  We weren't able to process this card. Please try a different card from your wallet.
                </p>
                <button
                  className="btn max-w-full w-full"
                  style={{height: 52, borderRadius: 12, fontSize: 16, fontWeight: 600}}
                  onClick={() => {
                    Analytics.track('Prepaid Try Different Card', {
                      code: product.code,
                      env: Environment.getVar('REACT_APP_ENV') ?? 'test',
                      email: token,
                    });
                    setPrepaidBlockMode(null);
                  }}
                >
                  Try a Different Card
                </button>
              </>
            ) : (
              <>
                <p className="text-gray-400 text-sm mb-6" style={{maxWidth: 280}}>
                  We weren't able to process this card. Finish setup in the app.
                </p>
                <a
                  href="nyx://"
                  onClick={() =>
                    Analytics.track('Prepaid Routed to App', {
                      code: product.code,
                      env: Environment.getVar('REACT_APP_ENV') ?? 'test',
                      email: token,
                    })
                  }
                  className="btn max-w-full w-full flex items-center justify-center gap-2"
                  style={{height: 52, borderRadius: 12, fontSize: 16, fontWeight: 600, textDecoration: 'none'}}
                >
                  Continue in the App
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" style={{marginLeft: 4}}>
                    <path
                      d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6M15 3h6v6M10 14L21 3"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </a>
              </>
            )}
          </div>
        </BottomSheet>

        <MaybeStagingBadge />
      </div>
    </>
  );
};

export default GraduationPaywallScreen;
